import { useState, useEffect, useMemo } from 'react';
import { SectionHeader, PremiumTable } from '@/components/dashboard/MetricCard';
import { useDashboard } from '@/context/DashboardContext';
import { dashboardApi } from '@/services/dashboardApi';
import { fmtMoney, fmtPct, normCurrency, MEMBERSHIP_PRICES, LIFETIME_PRICES } from '@/lib/format';
import { ChevronDown, ChevronUp, Copy, Check, Phone, Video, Handshake, Smartphone, Home, Landmark, Coins, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSiteLang } from '@/components/site/SiteLang';

/*
  Broker Success Kit — uso interno de brokers (solo Premium).
  Contenido revisado el 09/10/2026 contra el canon de FHC:
  - Precios en la divisa del miembro (EUR en el EEE, USD fuera).
  - Porcentajes y días de exigibilidad leídos del CDM (/commissions → rates, payable_days);
    los valores de abajo solo se usan si el servidor no responde.
  - Sin «tokens» ni «FHCT»: se habla de «aportaciones».
*/

// Valores canónicos de respaldo (Plan Comercial v2, desde el 20/09/2026)
const FALLBACK_RATES = { n1: 15, n2: 10, n3: 5, fhct: { n1: 2.5, n2: 1.5, n3: 0.5 } };
const LEGACY_RATES = [10, 5, 2.5];   // ventas anteriores al 20/09/2026
const BRICK_RATES = [20, 10, 5];     // el backend aún no expone esta tabla
const CAP_PCT = 30;
const FALLBACK_PAYABLE_DAYS = 15;
// Mínimo por operación (Samuel, 08/10/2026): 1.000 para PRO y Premium y 5.000 para Access, en la moneda
// de la operación: euros si está en Europa (todas las de este año, en España) y dólares fuera.
const MIN_APORT = { pro: 1000, access: 5000 };

function minAport(n) {
  return fmtMoney(n, 'eur');
}

function buildContent(cur, lang) {
  return lang === 'en' ? buildContentEn(cur) : buildContentEs(cur);
}

function buildContentEs(cur) {
  const other = cur === 'eur' ? 'usd' : 'eur';
  const P = MEMBERSHIP_PRICES[cur];
  const PO = MEMBERSHIP_PRICES[other];
  const L = LIFETIME_PRICES[cur];
  const LO = LIFETIME_PRICES[other];
  const m = (v) => fmtMoney(v, cur);
  const mo = (v) => fmtMoney(v, other);
  const region = cur === 'eur' ? 'en el EEE' : 'fuera del EEE';
  const otherRegion = cur === 'eur' ? 'fuera del EEE' : 'en el EEE';
  const lifetime = `+${m(L.popup)} en la llamada o +${m(L.late)} en los 30 días siguientes (${otherRegion}: +${mo(LO.popup)} o +${mo(LO.late)})`;
  const minPro = minAport(MIN_APORT.pro);
  const minAccess = minAport(MIN_APORT.access);
  const scriptMin = minAport(MIN_APORT.pro);

  const ecoItems = [
    { icon: Home, name: 'Freedom Homes Club', role: 'La puerta de entrada', desc: 'Club privado con membresía anual: comunidad, formación y acceso a oportunidades inmobiliarias off-market seleccionadas por el club.' },
    { icon: Coins, name: 'Aportación', role: 'La decisión del miembro', desc: 'Es el dinero que el miembro decide aportar a una operación concreta del club. Se formaliza con un contrato privado vinculado a un inmueble real.' },
    { icon: Landmark, name: 'Operación inmobiliaria', role: 'El activo real', desc: 'El club adquiere inmuebles completos en propiedad. La ejecución (reforma, alquiler o venta) la llevan operadores especializados.' },
    { icon: Zap, name: '2kenize', role: 'La tecnología', desc: 'Plataforma tecnológica propia que registra y gestiona las aportaciones y su trazabilidad dentro del club.' },
  ];

  const products = {
    fhc: {
      icon: Home, name: 'Freedom Homes Club', tagline: 'Club privado · Comunidad · Formación',
      whatItIs: 'Un club privado con membresía anual que da acceso a oportunidades inmobiliarias off-market seleccionadas, formación para miembros y brokers, y una comunidad de miembros, brokers y operadores. No es un fondo ni un banco: es la puerta de entrada al club.',
      script: `"Las operaciones inmobiliarias buenas suelen pedir mucho capital para entrar. En Freedom Homes Club se puede participar desde ${scriptMin}.\n\nEs un club privado. No compras un piso entero: entras en un club que selecciona operaciones, te forma para entenderlas y te deja decidir en cuáles participas.\n\nLa membresía es anual. PRO y Premium se pueden hacer vitalicias en esta misma llamada."`,
      profiles: ['Emprendedor', 'Profesional con capital disponible', 'Broker inmobiliario', 'Formador o comercial independiente', 'Ahorrador que busca activos reales'],
      benefits: [['Acceso a operaciones', 'Oportunidades off-market seleccionadas por el club'], ['Comunidad', 'Miembros, brokers y operadores'], ['Formación', 'FHC Academy y sesiones en directo'], ['Decisión propia', 'El miembro elige en qué operación participa'], ['Certificación', 'Bootcamp Broker Digital (incluido en Premium)'], ['Ventanas operativas', 'Aviso con 3 días y entrada durante 3–4 horas']],
      warnings: ['"Te garantizo un retorno del X %"', '"Es como comprar acciones"', '"Esto es un fondo de inversión"', '"Puedes sacar el dinero cuando quieras"', '"Vas a ganar dinero captando gente"'],
      miniFaq: [
        ['¿Qué es una membresía FHC?', 'El acceso al club privado: comunidad, formación y oportunidades. No es un producto financiero ni un fondo.'],
        ['¿Cuánto cuesta?', `Membresías anuales ${region}: Access ${m(P.starter)}, PRO ${m(P.basica)} y Premium ${m(P.premium)} (${otherRegion}: ${mo(PO.starter)}, ${mo(PO.basica)} y ${mo(PO.premium)}). PRO y Premium pueden pasar a vitalicia: ${lifetime}.`],
        ['¿Necesito saber de cripto o de inmobiliario?', 'No. La formación del club está pensada para quien quiere participar con criterio, no para expertos técnicos.'],
      ],
    },
    aport: {
      icon: Coins, name: 'Aportaciones', tagline: 'Aportación a operaciones · Contrato privado · Decisión del miembro',
      whatItIs: 'La aportación es el dinero que el miembro decide poner en una operación inmobiliaria del club. Se formaliza con un contrato privado vinculado a la compra de un inmueble concreto. Ninguna membresía incluye aportaciones: se hacen aparte, dentro del club, eligiendo siempre la operación.',
      script: '"Dentro del club, cuando se abre una ventana operativa, cada miembro decide si participa y con cuánto.\n\nEsa aportación se formaliza con un contrato privado vinculado a un inmueble real.\n\nHay una permanencia de 12 meses, y el capital se puede retirar cuando no está desplegado en una operación abierta."',
      profiles: ['Miembro con capital disponible', 'Profesional o emprendedor', 'Ahorrador que busca activos reales', 'Family office'],
      benefits: [['Activo real', 'Cada aportación va a un inmueble concreto'], ['Decisión del miembro', 'Eliges en qué operación entras'], ['Transparencia', 'El club se queda un 20 % fijo del resultado de cada operación'], ['Plan por tramo', 'El miembro recibe del 50 % al 80 % del resultado según su aportación'], ['Contrato claro', 'Contrato privado por operación, vinculado a un inmueble'], ['Mínimos', `Desde ${minPro} con PRO o Premium, o ${minAccess} con Access (en dólares si la operación está fuera de Europa). Una operación puede pedir más`]],
      warnings: ['"Te garantizo un X % de rentabilidad"', '"Es una criptomoneda" o "Es un token"', '"Es un ingreso pasivo"', '"Lo puedes sacar cuando quieras" (hay 12 meses de permanencia)', '"La membresía incluye aportaciones"'],
      miniFaq: [
        ['¿Qué recibo cuando hago una aportación?', 'Un contrato privado vinculado a la operación y al inmueble concretos en los que participas.'],
        ['¿Dónde opera el club?', 'Hoy el club opera en España. En paralelo se constituye la AG en Zug (Suiza), que agrupará la actividad.'],
        ['¿Cuánto se puede aportar?', `Desde ${minPro} con PRO o Premium, y desde ${minAccess} con Access. Siempre eligiendo la operación.`],
      ],
    },
    twokenize: {
      icon: Zap, name: '2kenize', tagline: 'Plataforma tecnológica · Infraestructura del club',
      whatItIs: 'La plataforma tecnológica propia del club. Registra y gestiona las aportaciones y su trazabilidad, y está pensada para escalar el modelo a otros mercados. No es una app para el usuario final: es el motor del club.',
      script: '"2kenize es la tecnología que está detrás del club. Es nuestra: controlamos la plataforma, los datos y la experiencia.\n\nPara perfiles más avanzados, como una promotora o un family office, existe un modelo de licencia en desarrollo."',
      profiles: ['Promotora inmobiliaria', 'Family office', 'Broker sénior', 'Aliado B2B', 'Socio tecnológico'],
      benefits: [['Tecnología propia', 'No dependemos de terceros'], ['Modelo de licencia', 'En desarrollo para promotoras y family offices'], ['Trazabilidad', 'Cada aportación queda registrada'], ['Escalabilidad', 'Replicar el modelo en otros mercados'], ['Diferencial', 'Activo tecnológico del club'], ['Propuesta B2B', 'Herramienta para alianzas sénior']],
      warnings: ['"2kenize sustituye a una blockchain"', '"Es como Ethereum pero inmobiliario"', '"Es gratis usarla"', '"La plataforma está terminada" (está en beta)', '"Cualquiera puede usarla" (acceso controlado)'],
      miniFaq: [
        ['¿2kenize está disponible ya?', 'Sí, como motor interno del club. El acceso externo mediante licencia está en desarrollo.'],
        ['¿Mi empresa puede usar 2kenize?', 'Existe un modelo de licencia en desarrollo para promotoras y family offices. Se valora caso a caso.'],
      ],
    },
  };

  const accessRows = [['Tipo', 'Anual (sin opción vitalicia)'], ['Comunidad FHC', 'Acceso completo'], ['FHC Academy', 'Nivel base'], ['Aportación mínima', `${minAccess} por operación`], ['Soporte', 'Máx. 48 h laborables'], ['Perfil ideal', 'Quien quiere conocer el club por dentro']];
  const proRows = [['Tipo', 'Anual (opción vitalicia)'], ['Comunidad FHC', 'Acceso completo'], ['FHC Academy', 'Nivel avanzado'], ['Aportación mínima', `${minPro} por operación`], ['Eventos', 'Sesiones online en directo'], ['Soporte', 'Máx. 24 h laborables'], ['Perfil ideal', 'Miembro que quiere participar en operaciones'], ['Narrativa', '"Tu acceso completo al club"']];
  const premiumRows = [['Tipo', 'Anual (opción vitalicia)'], ['Incluye', 'Todo lo de PRO'], ['Bootcamp', 'Broker Digital, 4 días presenciales'], ['Certificación', 'Certificación FHC para comercializar'], ['Alojamiento y dietas', 'No incluidos'], ['Soporte', 'Máx. 24 h laborables'], ['Perfil ideal', 'Futuro broker o miembro que quiere formarse a fondo'], ['Narrativa', '"Formación y certificación de broker"']];

  return { ecoItems, products, accessRows, proRows, premiumRows, lifetime, P, PO, m, mo, otherRegion };
}

// Versión en inglés británico (05/11/2026). Mismas cifras, porcentajes y niveles que la española.
function buildContentEn(cur) {
  const other = cur === 'eur' ? 'usd' : 'eur';
  const P = MEMBERSHIP_PRICES[cur];
  const PO = MEMBERSHIP_PRICES[other];
  const L = LIFETIME_PRICES[cur];
  const LO = LIFETIME_PRICES[other];
  const m = (v) => fmtMoney(v, cur);
  const mo = (v) => fmtMoney(v, other);
  const region = cur === 'eur' ? 'in the EEA' : 'outside the EEA';
  const otherRegion = cur === 'eur' ? 'outside the EEA' : 'in the EEA';
  const lifetime = `+${m(L.popup)} on the call or +${m(L.late)} within the following 30 days (${otherRegion}: +${mo(LO.popup)} or +${mo(LO.late)})`;
  const minPro = minAport(MIN_APORT.pro);
  const minAccess = minAport(MIN_APORT.access);
  const scriptMin = minAport(MIN_APORT.pro);

  const ecoItems = [
    { icon: Home, name: 'Freedom Homes Club', role: 'The way in', desc: 'A private club with an annual membership: community, training and access to off-market property opportunities selected by the club.' },
    { icon: Coins, name: 'Contribution', role: 'The member’s decision', desc: 'The money a member chooses to put into a specific club deal. It is formalised through a private contract linked to a real property.' },
    { icon: Landmark, name: 'Property deal', role: 'The real asset', desc: 'The club acquires whole properties outright. Execution (refurbishment, letting or sale) is handled by specialist operators.' },
    { icon: Zap, name: '2kenize', role: 'The technology', desc: 'The club’s own technology platform, which records and manages contributions and their traceability within the club.' },
  ];

  const products = {
    fhc: {
      icon: Home, name: 'Freedom Homes Club', tagline: 'Private club · Community · Training',
      whatItIs: 'A private club with an annual membership that gives access to selected off-market property opportunities, training for members and brokers, and a community of members, brokers and operators. It is not a fund or a bank: it is the way into the club.',
      script: `‘Good property deals usually need a lot of capital to get in. At Freedom Homes Club you can take part from ${scriptMin}.\n\nIt’s a private club. You’re not buying a whole flat: you’re joining a club that selects deals, trains you to understand them and lets you decide which ones you take part in.\n\nThe membership is annual. PRO and Premium can be made lifetime on this very call.’`,
      profiles: ['Entrepreneur', 'Professional with available capital', 'Property broker', 'Independent trainer or salesperson', 'Saver looking for real assets'],
      benefits: [['Access to deals', 'Off-market opportunities selected by the club'], ['Community', 'Members, brokers and operators'], ['Training', 'FHC Academy and live sessions'], ['Your own decision', 'The member chooses which deal to take part in'], ['Certification', 'Digital Broker Bootcamp (included in Premium)'], ['Operating windows', '3 days’ notice, then open for 3–4 hours']],
      warnings: ['‘I guarantee you an X% return’', '‘It’s like buying shares’', '‘This is an investment fund’', '‘You can take your money out whenever you like’', '‘You’ll make money by signing people up’'],
      miniFaq: [
        ['What is an FHC membership?', 'Access to the private club: community, training and opportunities. It is not a financial product or a fund.'],
        ['How much does it cost?', `Annual memberships ${region}: Access ${m(P.starter)}, PRO ${m(P.basica)} and Premium ${m(P.premium)} (${otherRegion}: ${mo(PO.starter)}, ${mo(PO.basica)} and ${mo(PO.premium)}). PRO and Premium can be made lifetime: ${lifetime}.`],
        ['Do I need to know about crypto or property?', 'No. The club’s training is designed for people who want to take part with sound judgement, not for technical experts.'],
      ],
    },
    aport: {
      icon: Coins, name: 'Contributions', tagline: 'Contributions to deals · Private contract · Member’s decision',
      whatItIs: 'A contribution is the money a member chooses to put into one of the club’s property deals. It is formalised through a private contract linked to the purchase of a specific property. No membership includes contributions: they are made separately, within the club, and the member always chooses the deal.',
      script: '‘Inside the club, when an operating window opens, each member decides whether to take part and with how much.\n\nThat contribution is formalised through a private contract linked to a real property.\n\nThere’s a 12-month minimum term, and capital can be withdrawn when it isn’t committed to an open deal.’',
      profiles: ['Member with available capital', 'Professional or entrepreneur', 'Saver looking for real assets', 'Family office'],
      benefits: [['Real asset', 'Every contribution goes into a specific property'], ['Member’s decision', 'You choose which deal you join'], ['Transparency', 'The club keeps a fixed 20% of each deal’s result'], ['Plan by bracket', 'The member receives 50% to 80% of the result, depending on their contribution'], ['Clear contract', 'A private contract per deal, linked to a property'], ['Minimums', `From ${minPro} with PRO or Premium, or ${minAccess} with Access (in dollars if the deal is outside Europe). A deal may require more`]],
      warnings: ['‘I guarantee you an X% return’', '‘It’s a cryptocurrency’ or ‘It’s a token’', '‘It’s passive income’', '‘You can take it out whenever you like’ (there’s a 12-month minimum term)', '‘The membership includes contributions’'],
      miniFaq: [
        ['What do I get when I make a contribution?', 'A private contract linked to the specific deal and property you take part in.'],
        ['Where does the club operate?', 'Today the club operates in Spain. In parallel, the AG is being set up in Zug (Switzerland) to bring the activity together.'],
        ['How much can I contribute?', `From ${minPro} with PRO or Premium, and from ${minAccess} with Access. Always choosing the deal.`],
      ],
    },
    twokenize: {
      icon: Zap, name: '2kenize', tagline: 'Technology platform · The club’s infrastructure',
      whatItIs: 'The club’s own technology platform. It records and manages contributions and their traceability, and is designed to scale the model to other markets. It is not an end-user app: it is the engine behind the club.',
      script: '‘2kenize is the technology behind the club. It’s ours: we control the platform, the data and the experience.\n\nFor more advanced profiles, such as a property developer or a family office, a licensing model is in development.’',
      profiles: ['Property developer', 'Family office', 'Senior broker', 'B2B partner', 'Technology partner'],
      benefits: [['Our own technology', 'We don’t depend on third parties'], ['Licensing model', 'In development for developers and family offices'], ['Traceability', 'Every contribution is recorded'], ['Scalability', 'Replicating the model in other markets'], ['Differentiator', 'A technology asset owned by the club'], ['B2B proposition', 'A tool for senior partnerships']],
      warnings: ['‘2kenize replaces a blockchain’', '‘It’s like Ethereum, but for property’', '‘It’s free to use’', '‘The platform is finished’ (it’s in beta)', '‘Anyone can use it’ (access is controlled)'],
      miniFaq: [
        ['Is 2kenize available now?', 'Yes, as the club’s internal engine. External access through a licence is in development.'],
        ['Can my company use 2kenize?', 'A licensing model is in development for property developers and family offices. Each case is assessed individually.'],
      ],
    },
  };

  const accessRows = [['Type', 'Annual (no lifetime option)'], ['FHC community', 'Full access'], ['FHC Academy', 'Core level'], ['Minimum contribution', `${minAccess} per deal`], ['Support', 'Within 48 working hours'], ['Ideal profile', 'Someone who wants to see the club from the inside']];
  const proRows = [['Type', 'Annual (lifetime option)'], ['FHC community', 'Full access'], ['FHC Academy', 'Advanced level'], ['Minimum contribution', `${minPro} per deal`], ['Events', 'Live online sessions'], ['Support', 'Within 24 working hours'], ['Ideal profile', 'A member who wants to take part in deals'], ['Narrative', '‘Your full access to the club’']];
  const premiumRows = [['Type', 'Annual (lifetime option)'], ['Includes', 'Everything in PRO'], ['Bootcamp', 'Digital Broker, 4 days in person'], ['Certification', 'FHC certification to sell'], ['Accommodation and meals', 'Not included'], ['Support', 'Within 24 working hours'], ['Ideal profile', 'Future broker, or a member who wants in-depth training'], ['Narrative', '‘Broker training and certification’']];

  return { ecoItems, products, accessRows, proRows, premiumRows, lifetime, P, PO, m, mo, otherRegion };
}

const FAQ_DATA_EN = [
  { group: 'About the club', items: [
    { id: 'f1', q: 'What exactly is Freedom Homes Club?', a: 'A private club that gives access to selected property opportunities, training and a community of members. It is not a fund or a bank.' },
    { id: 'f2', q: 'How do the club, contributions and 2kenize fit together?', a: 'FHC is the club and its membership. A contribution is the money a member puts into a specific deal. 2kenize is the technology that records it. They are all parts of the same club.' },
    { id: 'f3', q: 'What’s the difference between the membership and a contribution?', a: 'The membership is access to the club (annual). A contribution is optional, made separately, and the member always chooses the deal.' },
  ]},
  { group: 'About the structure', items: [
    { id: 'f4', q: 'Is this legal?', a: 'Yes. Today the club operates in Spain as a private, invitation-only club, with mandatory training beforehand and active participation by the member. In parallel, the AG is being set up in Zug (Switzerland).' },
    { id: 'f5', q: 'What exactly am I buying?', a: 'An annual membership giving access to the club. If you later decide to contribute to a deal, that contribution is formalised through a private contract. Neither is a share.' },
    { id: 'f6', q: 'Where is the international structure being set up?', a: 'In Zug (Switzerland), through an AG that will bring the club’s activity together.' },
  ]},
  { group: 'About the broker and the process', items: [
    { id: 'f7', q: 'What does a broker do?', a: 'Presents the club, supports their clients and, once certified, can build a team of up to 3 levels. The compensation plan is a possibility after certification, not the reason to join.' },
    { id: 'f8', q: 'How do people join?', a: 'Invitation → choice of membership → payment → welcome → training. The broker supports every stage.' },
    { id: 'f9', q: 'What happens after joining?', a: 'Access to the community, the Academy, the materials and support from the team. With Premium, the Bootcamp and broker certification as well.' },
  ]},
];

const GUIDE_STEPS_EN = [
  ['1', 'The club (2 min)', 'Explain the map: the club, the contribution and the technology (2kenize). Give the other person the big picture before going into detail.'],
  ['2', 'Fact sheet (3–5 min)', 'Choose the sheet most relevant to that profile. Use the script word for word on the first call.'],
  ['3', 'Next step (1 min)', 'Always propose a concrete step: a welcome call, a place at the Bootcamp or sending the information pack.'],
  ['4', 'Don’t overload', 'Don’t explain everything on one call. Pick one sheet. If they ask about another: ‘We’ll cover that in the next step.’'],
];

// Textos de interfaz (es / en)
const T = {
  es: {
    subtitle: 'Fichas, guiones, objeciones, membresías y comisiones · Actualizado el 09/10/2026',
    tabsAria: 'Secciones del kit',
    tabs: { ecosistema: 'El club', fichas: 'Fichas', membresias: 'Membresías', comisiones: 'Comisiones', faq: 'Preguntas frecuentes', guia: 'Guía de uso' },
    copyPrompt: 'Copia el texto:',
    ecoTitle: 'Cuatro piezas. Un club.',
    ecoSub: 'Antes de presentar nada, sitúa a tu interlocutor. Esto es lo que conecta todo.',
    sheetsTitle: 'Tres fichas. Una explicación clara.',
    sheetsSub: 'Elige la ficha que mejor encaja con el perfil de tu interlocutor y úsala en la llamada.',
    whatItIs: 'Qué es', howToSay: 'Cómo lo dices en la llamada', copyScript: 'Copiar guion', idealProfile: 'Perfil ideal',
    keyBenefits: 'Beneficios clave', whatNotToSay: 'Qué no decir', quickQuestions: 'Preguntas rápidas',
    membTitle: 'Membresías',
    membSub: (lifetime) => <>Membresías anuales desde el 20/09/2026. PRO y Premium pueden pasar a vitalicia: {lifetime}. Access no tiene opción vitalicia.</>,
    membCurrency: 'Cada cliente paga en la divisa de su residencia: euros en el EEE, dólares fuera.',
    membCard: { starter: 'Membresía Access', basica: 'Membresía PRO', premium: 'Membresía Premium' },
    perYear: '/ año',
    commTitle: 'Plan de compensación del broker',
    commSub: (cap) => <>Plan Comercial v2, vigente desde el 20/09/2026 y disponible una vez certificado. Las comisiones de una venta nunca superan el {cap} del importe.</>,
    commCols: ['Nivel', 'Comisión', 'Sobre'],
    commRows: [
      ['N1 · Venta directa', 'Membresías, paso a vitalicia, upgrades y renovaciones de tus clientes'],
      ['N2 · Segundo nivel', 'Lo mismo, vendido por brokers que incorporaste'],
      ['N3 · Tercer nivel', 'Lo mismo, vendido por los brokers que incorporaron ellos'],
      ['Aportaciones', 'N1 / N2 / N3 sobre el importe aportado'],
      ['Bricks', 'N1 / N2 / N3 sobre lo aportado, en puntos (sin valor monetario)'],
    ],
    whenPaid: 'Cuándo se cobra:',
    whenPaidText: (days) => <> una comisión es exigible {`${days} días después del pago del 100 % y la firma de los T&C`} por parte del cliente.</>,
    currency: 'En qué divisa:',
    currencyText: ' se paga en la misma divisa en la que pagó el cliente (euros o dólares), sin conversión.',
    legacy: 'Ventas anteriores al 20/09/2026:',
    legacyText: (r) => <> conservan la tarifa anterior, {r}.</>,
    bricks: 'Bricks:',
    bricksText: ' son puntos de fidelización. No tienen valor monetario ni se pueden vender o canjear por dinero.',
    exampleTitle: 'Ejemplo de cálculo',
    example: (e) => (
      <>
        Una membresía PRO ({e.price}) vendida por ti genera <strong className="text-white">{e.a1}</strong> (N1, {e.p1}).<br />
        Si la vende un broker que incorporaste, te corresponden <strong className="text-white">{e.a2}</strong> (N2, {e.p2}).<br />
        Si la vende un broker de su equipo, <strong className="text-white">{e.a3}</strong> (N3, {e.p3}).
      </>
    ),
    exampleNote: 'Ejemplo sobre el precio de la membresía. No es una previsión de ingresos.',
    faqTitle: 'Preguntas frecuentes',
    faqSub: 'Respuestas breves para llamadas, WhatsApp y Zoom, alineadas con los datos del club.',
    copied: 'Copiado', copyAnswer: 'Copiar respuesta',
    guideTitle: 'Cómo usar este kit en una llamada',
    guideSub: 'Protocolo para presentar con claridad y acordar el siguiente paso.',
    guideCards: [
      ['En una llamada de prospección', 'Empieza por el club. Elige una ficha. Propón el siguiente paso.'],
      ['En un Zoom de presentación', 'Comparte pantalla y navega por la ficha más relevante.'],
      ['En una reunión 1:1', 'Muestra el kit en tableta o portátil y deja que tu interlocutor lo explore.'],
      ['En el seguimiento por WhatsApp', 'Envía una sola ficha con un mensaje de contexto breve.'],
    ],
    closeTitle: 'Cierre recomendado',
    closes: ['"¿Tiene sentido para ti lo que te he explicado hasta ahora?"', '"¿Quieres que te ponga en contacto con el equipo de bienvenida?"', '"¿Te reservo plaza en la próxima sesión de bienvenida?"'],
    closeNote: 'El cierre no es presión: es una invitación a dar el siguiente paso con claridad.',
  },
  en: {
    subtitle: 'Fact sheets, scripts, objections, memberships and commissions · Updated 09/10/2026',
    tabsAria: 'Kit sections',
    tabs: { ecosistema: 'The club', fichas: 'Fact sheets', membresias: 'Memberships', comisiones: 'Commissions', faq: 'FAQs', guia: 'How to use it' },
    copyPrompt: 'Copy the text:',
    ecoTitle: 'Four pieces. One club.',
    ecoSub: 'Before you present anything, give the other person the big picture. This is what ties it all together.',
    sheetsTitle: 'Three fact sheets. One clear explanation.',
    sheetsSub: 'Choose the sheet that best fits the other person’s profile and use it on the call.',
    whatItIs: 'What it is', howToSay: 'How to say it on the call', copyScript: 'Copy script', idealProfile: 'Ideal profile',
    keyBenefits: 'Key benefits', whatNotToSay: 'What not to say', quickQuestions: 'Quick questions',
    membTitle: 'Memberships',
    membSub: (lifetime) => <>Annual memberships, in force since 20/09/2026. PRO and Premium can be made lifetime: {lifetime}. Access has no lifetime option.</>,
    membCurrency: 'Each client pays in the currency of their country of residence: euros in the EEA, dollars outside it.',
    membCard: { starter: 'Access membership', basica: 'PRO membership', premium: 'Premium membership' },
    perYear: '/ year',
    commTitle: 'Broker compensation plan',
    commSub: (cap) => <>Commercial Plan v2, in force since 20/09/2026 and available once you are certified. Commissions on a sale never exceed {cap} of the amount.</>,
    commCols: ['Level', 'Commission', 'Applies to'],
    commRows: [
      ['N1 · Direct sales', 'Your clients’ memberships, lifetime conversions, upgrades and renewals'],
      ['N2 · Second level', 'The same, sold by brokers you brought in'],
      ['N3 · Third level', 'The same, sold by the brokers they brought in'],
      ['Contributions', 'N1 / N2 / N3 on the amount contributed'],
      ['Bricks', 'N1 / N2 / N3 on the amount contributed, in points (no monetary value)'],
    ],
    whenPaid: 'When it’s paid:',
    whenPaidText: (days) => <> a commission becomes payable {days} days after the client has paid 100% and signed the T&Cs.</>,
    currency: 'Currency:',
    currencyText: ' it is paid in the same currency the client paid in (euros or dollars), with no conversion.',
    legacy: 'Sales before 20/09/2026:',
    legacyText: (r) => <> keep the previous rates, {r}.</>,
    bricks: 'Bricks:',
    bricksText: ' these are loyalty points. They have no monetary value and cannot be sold or exchanged for money.',
    exampleTitle: 'Worked example',
    example: (e) => (
      <>
        A PRO membership ({e.price}) sold by you generates <strong className="text-white">{e.a1}</strong> (N1, {e.p1}).<br />
        If it’s sold by a broker you brought in, you receive <strong className="text-white">{e.a2}</strong> (N2, {e.p2}).<br />
        If it’s sold by a broker in their team, <strong className="text-white">{e.a3}</strong> (N3, {e.p3}).
      </>
    ),
    exampleNote: 'Example based on the membership price. This is not an income forecast.',
    faqTitle: 'Frequently asked questions',
    faqSub: 'Short answers for calls, WhatsApp and Zoom, consistent with the club’s information.',
    copied: 'Copied', copyAnswer: 'Copy answer',
    guideTitle: 'How to use this kit on a call',
    guideSub: 'A protocol for presenting clearly and agreeing the next step.',
    guideCards: [
      ['On a prospecting call', 'Start with the club. Pick one sheet. Propose the next step.'],
      ['On a Zoom presentation', 'Share your screen and walk through the most relevant sheet.'],
      ['In a one-to-one meeting', 'Show the kit on a tablet or laptop and let the other person explore it.'],
      ['When following up on WhatsApp', 'Send a single sheet with a short message for context.'],
    ],
    closeTitle: 'Recommended close',
    closes: ['‘Does what I’ve explained so far make sense to you?’', '‘Would you like me to put you in touch with the welcome team?’', '‘Shall I book you a place at the next welcome session?’'],
    closeNote: 'Closing isn’t pressure: it’s an invitation to take the next step with clarity.',
  },
};

const FAQ_DATA = [
  { group: 'Sobre el club', items: [
    { id: 'f1', q: '¿Qué es exactamente Freedom Homes Club?', a: 'Un club privado que da acceso a oportunidades inmobiliarias seleccionadas, formación y una comunidad de miembros. No es un fondo ni un banco.' },
    { id: 'f2', q: '¿Qué relación hay entre el club, las aportaciones y 2kenize?', a: 'FHC es el club y su membresía. La aportación es el dinero que el miembro pone en una operación concreta. 2kenize es la tecnología que lo registra. Son piezas del mismo club.' },
    { id: 'f3', q: '¿Qué diferencia hay entre la membresía y la aportación?', a: 'La membresía es el acceso al club (anual). La aportación es opcional, se hace aparte y siempre eligiendo la operación.' },
  ]},
  { group: 'Sobre la estructura', items: [
    { id: 'f4', q: '¿Esto es legal?', a: 'Sí. Hoy el club opera en España como club privado, por invitación, con formación previa obligatoria y participación activa del miembro. En paralelo se constituye la AG en Zug (Suiza).' },
    { id: 'f5', q: '¿Qué estoy comprando exactamente?', a: 'Una membresía anual de acceso al club. Si después decides aportar a una operación, esa aportación se formaliza con un contrato privado. Ninguna de las dos es una acción.' },
    { id: 'f6', q: '¿Dónde se constituye la estructura internacional?', a: 'En Zug (Suiza), mediante una AG que agrupará la actividad del club.' },
  ]},
  { group: 'Sobre el broker y el proceso', items: [
    { id: 'f7', q: '¿Qué hace un broker?', a: 'Presenta el club, acompaña a sus clientes y, una vez certificado, puede formar un equipo de hasta 3 niveles. El plan de compensación es una posibilidad tras la certificación, no la razón para entrar.' },
    { id: 'f8', q: '¿Cómo se entra?', a: 'Invitación → elección de membresía → pago → bienvenida → formación. El broker acompaña cada etapa.' },
    { id: 'f9', q: '¿Qué pasa después de entrar?', a: 'Acceso a la comunidad, la Academia, los materiales y el soporte del equipo. Con Premium, además, el Bootcamp y la certificación de broker.' },
  ]},
];

const GUIDE_STEPS = [
  ['1', 'El club (2 min)', 'Explica el mapa: el club, la aportación y la tecnología (2kenize). Sitúa a tu interlocutor antes de entrar en detalle.'],
  ['2', 'Ficha (3–5 min)', 'Elige la ficha más relevante para ese perfil. Usa el guion tal cual en la primera llamada.'],
  ['3', 'Siguiente paso (1 min)', 'Propón siempre un paso concreto: llamada de bienvenida, plaza en el Bootcamp o envío del dosier.'],
  ['4', 'No saturar', 'No expliques todo en la misma llamada. Elige una ficha. Si preguntan por otra: "Eso lo vemos en el siguiente paso".'],
];

const pillCls = (active) => `inline-flex items-center gap-2 min-h-[40px] px-4 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 ${active ? 'bg-[#c9a84c] text-[#0a1628] shadow-lg shadow-[#c9a84c]/15' : 'bg-white/[0.04] text-gray-400 hover:text-gray-300 hover:bg-white/[0.06] border border-white/[0.06]'}`;

export default function DashboardSuccessKit() {
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  const faqData = lang === 'en' ? FAQ_DATA_EN : FAQ_DATA;
  const guideSteps = lang === 'en' ? GUIDE_STEPS_EN : GUIDE_STEPS;
  const { membership } = useDashboard();
  const cur = normCurrency(membership?.currency);
  const [activeTab, setActiveTab] = useState('fhc');
  const [openFaq, setOpenFaq] = useState(null);
  const [copied, setCopied] = useState(null);
  const [section, setSection] = useState('ecosistema');
  const [cdm, setCdm] = useState({ rates: null, payableDays: null });

  // Tarifas vigentes y días de exigibilidad desde la configuración del CDM
  useEffect(() => {
    let alive = true;
    dashboardApi.commissions()
      .then((r) => { if (alive) setCdm({ rates: r?.rates || null, payableDays: r?.payable_days ?? null }); })
      .catch(() => { /* se usan los valores canónicos de respaldo */ });
    return () => { alive = false; };
  }, []);

  const c = useMemo(() => buildContent(cur, lang), [cur, lang]);
  const rates = cdm.rates || FALLBACK_RATES;
  const memb = [rates.n1, rates.n2, rates.n3].map((v, i) => (v == null ? [15, 10, 5][i] : v));
  const fh = rates.fhct || FALLBACK_RATES.fhct;
  const aport = [fh.n1, fh.n2, fh.n3].map((v, i) => (v == null ? FALLBACK_RATES.fhct[`n${i + 1}`] : v));
  const days = cdm.payableDays || FALLBACK_PAYABLE_DAYS;
  // es: «2,5 / 1,5 / 0,5 %» · en: «2.5 / 1.5 / 0.5%»
  const slash = (arr) => (lang === 'en'
    ? `${arr.map((v) => fmtPct(v).replace('%', '')).join(' / ')}%`
    : `${arr.map((v) => fmtPct(v).replace(' %', '')).join(' / ')} %`);

  const comisiones = [
    [t.commRows[0][0], fmtPct(memb[0]), t.commRows[0][1]],
    [t.commRows[1][0], fmtPct(memb[1]), t.commRows[1][1]],
    [t.commRows[2][0], fmtPct(memb[2]), t.commRows[2][1]],
    [t.commRows[3][0], slash(aport), t.commRows[3][1]],
    [t.commRows[4][0], slash(BRICK_RATES), t.commRows[4][1]],
  ];

  const tabs = ['ecosistema', 'fichas', 'membresias', 'comisiones', 'faq', 'guia'].map((id) => ({ id, label: t.tabs[id] }));

  const copyText = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      window.prompt(t.copyPrompt, text);
    }
  };
  const p = c.products[activeTab];
  const pro = c.P.basica;

  return (
    <div className="space-y-6" data-testid="dashboard-success-kit">
      <SectionHeader title="Broker Success Kit" subtitle={t.subtitle} />

      <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1" role="tablist" aria-label={t.tabsAria}>
        {tabs.map(tab => (
          <button key={tab.id} type="button" role="tab" aria-selected={section === tab.id} onClick={() => setSection(tab.id)} className={pillCls(section === tab.id)}>{tab.label}</button>
        ))}
      </div>

      {section === 'ecosistema' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div>
            <h2 className="text-xl font-bold text-white mb-1">{t.ecoTitle}</h2>
            <p className="text-sm text-gray-400">{t.ecoSub}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {c.ecoItems.map((item, i) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: Math.min(i, 6) * 0.03 }}
                className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 hover:border-[#c9a84c]/20 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-[#c9a84c]/[0.08] flex items-center justify-center mb-3">
                  <item.icon size={18} className="text-[#c9a84c]" />
                </div>
                <h3 className="text-white font-semibold mb-0.5">{item.name}</h3>
                <p className="text-[#c9a84c] text-[11px] uppercase tracking-wider font-medium mb-2">{item.role}</p>
                <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {section === 'fichas' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div>
            <h2 className="text-xl font-bold text-white mb-1">{t.sheetsTitle}</h2>
            <p className="text-sm text-gray-400">{t.sheetsSub}</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[['fhc', c.products.fhc.name, Home], ['aport', c.products.aport.name, Coins], ['twokenize', c.products.twokenize.name, Zap]].map(([id, label, Icon]) => (
              <button key={id} type="button" onClick={() => setActiveTab(id)} aria-pressed={activeTab === id} className={pillCls(activeTab === id)}>
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>

          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
            <div className="p-5 sm:p-6 border-b border-white/[0.04] flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#c9a84c]/[0.08] flex items-center justify-center flex-shrink-0"><p.icon size={22} className="text-[#c9a84c]" /></div>
              <div className="min-w-0"><h3 className="text-white font-bold text-lg">{p.name}</h3><p className="text-gray-400 text-sm">{p.tagline}</p></div>
            </div>
            <div className="p-5 sm:p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                <Block title={t.whatItIs}><p className="text-gray-400 text-sm leading-relaxed">{p.whatItIs}</p></Block>
                <Block title={t.howToSay}>
                  <div className="bg-[#c9a84c]/[0.04] border border-[#c9a84c]/15 rounded-xl p-4 pr-14 relative">
                    <p className="text-gray-300 text-sm leading-relaxed italic whitespace-pre-line">{p.script}</p>
                    <button type="button" onClick={() => copyText('script', p.script)} aria-label={t.copyScript} className="absolute top-2 right-2 w-10 h-10 flex items-center justify-center rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-gray-400 hover:text-white transition-all">
                      {copied === 'script' ? <Check size={15} className="text-emerald-400" /> : <Copy size={15} />}
                    </button>
                  </div>
                </Block>
                <Block title={t.idealProfile}>
                  <div className="flex flex-wrap gap-1.5">{p.profiles.map(pr => <span key={pr} className="px-3 py-1 rounded-full text-[11px] uppercase tracking-wider font-medium bg-[#c9a84c]/[0.06] text-[#c9a84c] border border-[#c9a84c]/15">{pr}</span>)}</div>
                </Block>
              </div>
              <div className="space-y-6">
                <Block title={t.keyBenefits}>
                  <div className="space-y-3">{p.benefits.map(([bt, s]) => (
                    <div key={bt} className="flex gap-3"><div className="w-1 h-1 rounded-full bg-[#c9a84c] mt-2 flex-shrink-0" /><div><p className="text-white text-sm font-medium">{bt}</p><p className="text-gray-400 text-xs">{s}</p></div></div>
                  ))}</div>
                </Block>
                <Block title={t.whatNotToSay}>
                  <div className="bg-red-500/[0.04] border border-red-500/15 rounded-xl p-4 space-y-1.5">
                    {p.warnings.map((w, i) => <p key={i} className="text-red-400/80 text-sm flex items-start gap-2"><span className="text-red-500/60 mt-0.5" aria-hidden="true">&#x2717;</span> {w}</p>)}
                  </div>
                </Block>
                <Block title={t.quickQuestions}>
                  <div className="space-y-2">{p.miniFaq.map(([q, a]) => (
                    <div key={q} className="bg-white/[0.02] border border-white/[0.04] rounded-xl p-3"><p className="text-white text-sm font-medium mb-1">{q}</p><p className="text-gray-400 text-xs leading-relaxed">{a}</p></div>
                  ))}</div>
                </Block>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {section === 'membresias' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div>
            <h2 className="text-xl font-bold text-white mb-1">{t.membTitle}</h2>
            <p className="text-sm text-gray-400">{t.membSub(c.lifetime)}</p>
            <p className="text-xs text-gray-500 mt-1">{t.membCurrency}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <MembershipCard title={t.membCard.starter} perYear={t.perYear} price={c.m(c.P.starter)} alt={`${c.otherRegion}: ${c.mo(c.PO.starter)} ${t.perYear}`} rows={c.accessRows} />
            <MembershipCard title={t.membCard.basica} perYear={t.perYear} price={c.m(c.P.basica)} alt={`${c.otherRegion}: ${c.mo(c.PO.basica)} ${t.perYear}`} rows={c.proRows} />
            <MembershipCard title={t.membCard.premium} perYear={t.perYear} price={c.m(c.P.premium)} alt={`${c.otherRegion}: ${c.mo(c.PO.premium)} ${t.perYear}`} rows={c.premiumRows} featured />
          </div>
        </motion.div>
      )}

      {section === 'comisiones' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div>
            <h2 className="text-xl font-bold text-white mb-1">{t.commTitle}</h2>
            <p className="text-sm text-gray-400">{t.commSub(fmtPct(CAP_PCT))}</p>
          </div>
          <PremiumTable
            cols={t.commCols}
            rows={comisiones.map(([nivel, pct, sobre]) => [
              <span className="whitespace-nowrap">{nivel}</span>,
              <span className="text-[#c9a84c] font-bold whitespace-nowrap">{pct}</span>,
              sobre,
            ])}
          />
          <ul className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-2 text-sm text-gray-400 leading-relaxed list-disc pl-9">
            <li><strong className="text-white font-medium">{t.whenPaid}</strong>{t.whenPaidText(days)}</li>
            <li><strong className="text-white font-medium">{t.currency}</strong>{t.currencyText}</li>
            <li><strong className="text-white font-medium">{t.legacy}</strong>{t.legacyText(slash(LEGACY_RATES))}</li>
            <li><strong className="text-white font-medium">{t.bricks}</strong>{t.bricksText}</li>
          </ul>
          <div className="rounded-2xl border border-[#c9a84c]/15 bg-[#c9a84c]/[0.03] p-5">
            <h4 className="text-white font-medium mb-2">{t.exampleTitle}</h4>
            <p className="text-gray-400 text-sm leading-relaxed">
              {t.example({
                price: c.m(pro),
                a1: c.m(pro * memb[0] / 100), p1: fmtPct(memb[0]),
                a2: c.m(pro * memb[1] / 100), p2: fmtPct(memb[1]),
                a3: c.m(pro * memb[2] / 100), p3: fmtPct(memb[2]),
              })}
            </p>
            <p className="text-gray-500 text-xs mt-2">{t.exampleNote}</p>
          </div>
        </motion.div>
      )}

      {section === 'faq' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div>
            <h2 className="text-xl font-bold text-white mb-1">{t.faqTitle}</h2>
            <p className="text-sm text-gray-400">{t.faqSub}</p>
          </div>
          {faqData.map(group => (
            <div key={group.group} className="space-y-2">
              <h3 className="text-[11px] text-[#c9a84c] font-semibold uppercase tracking-wider mb-3">{group.group}</h3>
              {group.items.map(item => (
                <div key={item.id} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
                  <button type="button" onClick={() => setOpenFaq(openFaq === item.id ? null : item.id)} aria-expanded={openFaq === item.id} className="w-full flex items-center justify-between p-4 text-left min-h-[48px]">
                    <span className="text-white text-sm font-medium pr-4">{item.q}</span>
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${openFaq === item.id ? 'bg-[#c9a84c]/10' : 'bg-white/[0.04]'}`}>
                      {openFaq === item.id ? <ChevronUp size={13} className="text-[#c9a84c]" /> : <ChevronDown size={13} className="text-gray-400" />}
                    </div>
                  </button>
                  <AnimatePresence>
                    {openFaq === item.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-3 border-t border-white/[0.04] pt-3">
                          <p className="text-gray-400 text-sm leading-relaxed">{item.a}</p>
                          <button type="button" onClick={() => copyText(item.id, `${item.q}\n${item.a}`)} className="mt-1 inline-flex items-center gap-1.5 min-h-[40px] text-[11px] uppercase tracking-wider text-gray-400 hover:text-gray-300 transition-colors">
                            {copied === item.id ? <><Check size={12} className="text-emerald-400" /> {t.copied}</> : <><Copy size={12} /> {t.copyAnswer}</>}
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          ))}
        </motion.div>
      )}

      {section === 'guia' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div>
            <h2 className="text-xl font-bold text-white mb-1">{t.guideTitle}</h2>
            <p className="text-sm text-gray-400">{t.guideSub}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[Phone, Video, Handshake, Smartphone].map((Icon, i) => [Icon, ...t.guideCards[i]]).map(([Icon, title, desc], i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: Math.min(i, 6) * 0.03 }}
                className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 text-center hover:border-[#c9a84c]/20 transition-colors duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-[#c9a84c]/[0.08] flex items-center justify-center mx-auto mb-3">
                  <Icon size={17} className="text-[#c9a84c]" />
                </div>
                <h4 className="text-white text-sm font-medium mb-1">{title}</h4>
                <p className="text-gray-400 text-xs">{desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="space-y-2">
            {guideSteps.map(([num, title, desc], i) => (
              <motion.div
                key={num}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.25, delay: 0.2 + i * 0.06 }}
                className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 flex items-start gap-4"
              >
                <div className="w-8 h-8 rounded-full bg-[#c9a84c]/[0.08] flex items-center justify-center text-[#c9a84c] font-bold text-sm flex-shrink-0">{num}</div>
                <div><h4 className="text-white text-sm font-semibold mb-1">{title}</h4><p className="text-gray-400 text-sm">{desc}</p></div>
              </motion.div>
            ))}
          </div>
          <div className="rounded-2xl border border-[#c9a84c]/15 bg-[#c9a84c]/[0.03] p-5">
            <h4 className="text-white font-medium mb-3">{t.closeTitle}</h4>
            <div className="space-y-2">
              {t.closes.map(line => (
                <p key={line} className="text-white text-sm font-medium">{line}</p>
              ))}
            </div>
            <p className="text-gray-500 text-xs mt-3">{t.closeNote}</p>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function Block({ title, children }) {
  return <div><h4 className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold mb-2">{title}</h4>{children}</div>;
}

function MembershipCard({ title, price, alt, rows, featured, perYear = '/ año' }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: featured ? 0.1 : 0 }}
      className={`rounded-2xl border overflow-hidden ${featured ? 'border-[#c9a84c]/25' : 'border-white/[0.06]'} bg-white/[0.02]`}
    >
      <div className={`p-5 ${featured ? 'bg-[#c9a84c]/[0.06]' : 'bg-white/[0.02]'}`}>
        <h3 className="text-white font-bold text-lg">{title}</h3>
        <p className="text-[#c9a84c] text-2xl font-bold">{price} <span className="text-sm font-normal text-gray-400">{perYear}</span></p>
        {alt && <p className="text-xs text-gray-500 mt-0.5 first-letter:uppercase">{alt}</p>}
      </div>
      <div className="divide-y divide-white/[0.03]">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-start justify-between gap-4 px-5 py-3">
            <span className="text-gray-400 text-sm flex-shrink-0">{k}</span>
            <span className="text-white text-sm font-medium text-right">{v}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}
